// Second pre-save guard on the add-trip form (alongside the past-date confirm).
// If a third pre-save check is added, evaluate consolidating into a unified
// pre-save pipeline or moving the duplicate-detection logic to a backend
// endpoint.

import { datadogLogs } from '@datadog/browser-logs';
import { Settings } from 'luxon';
import { useCallback, useState } from 'react';

import type { AddTripFormValues } from '../FormValues';
import type {
  CandidateTrip,
  DuplicateCriteria,
} from './buildDuplicateCriteria';
import { buildDuplicateCriteria } from './buildDuplicateCriteria';
import { useAppDispatch } from 'src/app/store/hooks';
import type { TripsListApiResponse } from 'src/common/external/bambi-api/tripsApi';
import { bambiApi as tripsApi } from 'src/common/external/bambi-api/tripsApi';

export type LegMatches = {
  legIndex: number;
  matches: CandidateTrip[];
};

export function useDuplicateTripCheck() {
  const dispatch = useAppDispatch();
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const reset = useCallback(() => {
    setError(null);
  }, []);

  const checkDuplicates = useCallback(
    async (values: AddTripFormValues): Promise<LegMatches[] | null> => {
      setIsChecking(true);
      setError(null);
      try {
        const orgZone =
          typeof Settings.defaultZone === 'string'
            ? Settings.defaultZone
            : Settings.defaultZone.name;

        const perLeg: DuplicateCriteria[] = values.trips.map((leg) =>
          buildDuplicateCriteria({
            passengerId: values.passenger.id,
            orgZone,
            excludeTripId: values.id || undefined,
            leg: {
              timing: leg.timing,
              pickupLocation: leg.pickupLocation,
              dropoffLocation: leg.dropoffLocation,
            },
          })
        );

        const keyFor = (c: DuplicateCriteria) => JSON.stringify(c.apiFilters);

        const uniqueKeys = new Map<string, DuplicateCriteria>();
        perLeg.forEach((c) => {
          if (!uniqueKeys.has(keyFor(c))) uniqueKeys.set(keyFor(c), c);
        });

        const keyToLegIndices = new Map<string, number[]>();
        perLeg.forEach((c, i) => {
          const k = keyFor(c);
          const existing = keyToLegIndices.get(k);
          if (existing) {
            existing.push(i);
          } else {
            keyToLegIndices.set(k, [i]);
          }
        });

        const responses = await Promise.all(
          Array.from(uniqueKeys.entries()).map(async ([key, c]) => {
            const data: TripsListApiResponse = await dispatch(
              tripsApi.endpoints.tripsList.initiate(c.apiFilters, {
                subscribe: false,
                forceRefetch: true,
              })
            ).unwrap();

            if (
              typeof data.count === 'number' &&
              data.count > data.results.length
            ) {
              // The list response is truncated. We can't tell whether a
              // matching duplicate is hiding on a later page, so silently
              // proceeding would risk a false negative. Surface this as a
              // check failure — the inline Retry / Save anyway UI already
              // handles it.
              const legIndices = keyToLegIndices.get(key) ?? [];
              legIndices.forEach((leg_index) => {
                datadogLogs.logger.warn(
                  'duplicate-trip-check: count exceeded pageSize',
                  {
                    feature: 'duplicate-trip-check',
                    expected_max: c.apiFilters.pageSize,
                    actual_count: data.count,
                    leg_index,
                  }
                );
              });
              throw new Error(
                `Duplicate trip check could not be completed because the trips list response was truncated (received ${data.results.length} of ${data.count} results).`
              );
            }

            return [key, data.results] as const;
          })
        );

        const responseByKey = new Map(responses);

        const legMatches: LegMatches[] = perLeg.map((c, legIndex) => ({
          legIndex,
          matches: (responseByKey.get(keyFor(c)) ?? []).filter(c.postFilter),
        }));

        return legMatches;
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
        return null;
      } finally {
        setIsChecking(false);
      }
    },
    [dispatch]
  );

  return { checkDuplicates, isChecking, error, reset };
}
